import { LogisticsProjectLookupComponent } from '../../../logistics-projects/logistics-project-lookup.component';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Location, CommonModule } from '@angular/common';
import { MenuItem, MessageService, SelectItem, ConfirmationService } from "primeng/api";
import { OrdersService } from "../../order.service";
import { SalesOrder } from "../sales-order.model";
import { PaymentTerm } from "../../models/payment-term.model";
import { SalesOrderLine } from "../sales-order-line.model";
import { Products } from "../../models/product.model";
import { forkJoin, map, of, switchMap, tap } from "rxjs";
// SelectChangeEvent is an interface, not a module.
// import {SelectChangeEvent} from "primeng/dropdown";
import { ProductVariant } from "../../models/product-variant.model";
import { Company } from "../../../company/company.model";
import { Customer } from "../../../customer/customer.model";
import { CustomerService } from "../../../customer/customer.service";
import { ContractsService } from "../../../contracts/contracts.service";
import { SessionStorageService } from "../../../services/session-storage.service";
import { ProductsService } from "../../product.service";
import { FlexFieldValue } from "../../../shared/flex-fields/flex-field.model";
import { currencyFractionDigits, orderLineAmounts, roundCurrency, unitPriceForTargetNet } from '../../../shared/utils/commercial-money';

// PrimeNG Modules
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { CardModule } from 'primeng/card';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TableModule } from 'primeng/table';
import { DatePickerModule } from 'primeng/datepicker';
import { TextareaModule } from 'primeng/textarea';
import { ProgressBarModule } from "primeng/progressbar";
import { DividerModule } from "primeng/divider";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { TooltipModule } from 'primeng/tooltip';
import { PartyService } from 'src/app/party/party.service';
import { Party } from 'src/app/party/party.model';
import { PartyType } from 'src/app/party/party-type.enum';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { EditorModule } from 'primeng/editor';
import { FlexFieldFormComponent } from 'src/app/shared/flex-fields/flex-field-form/flex-field-form.component';
import { Country } from 'src/app/models/common/country.model';
import { CustomerFormComponent } from 'src/app/customer/customer-form/customer-form.component';
import { CompanyFormComponent } from 'src/app/company/company-form/company-form.component';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { PageHeroComponent } from 'src/app/shared/page-layout/page-hero.component';
import { PageFormSectionComponent } from 'src/app/shared/page-layout/page-form-section.component';
import { PageMetricStripComponent } from 'src/app/shared/page-layout/page-metric-strip.component';
import { PageMetric } from 'src/app/shared/page-layout/page-layout.models';
import { LogisticsProjectsService } from 'src/app/logistics-projects/logistics-projects.service';
import { CrmClient, CrmClientCommunication } from 'src/app/crm/crm.model';
import { CrmService } from 'src/app/crm/crm.service';
import { EnterpriseFeatureService } from 'src/app/services/enterprise-features.service';
import { LazyLocationPickerDirective } from 'src/app/shared/location-picker/lazy-location-picker.directive';
import { AiParsedOrderDocument, AiParsedOrderLine, OrderDocumentAiService } from '../../order-document-ai.service';
import { FirebaseStorageService } from 'src/app/services/firebase-storage.service';
import { SalesOrderAttachment } from '../sales-order-attachment.model';
import { AddEditProductComponent } from '../../product/add-edit-product/add-edit-product.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ExecutionOrderLineage } from '../execution-sales-order.model';
import { CommercialPartyPickerComponent } from 'src/app/shared/commercial-party-picker/commercial-party-picker.component';
import { CommercialPartyReference } from 'src/app/shared/commercial-party-picker/commercial-party.model';
import { CrmQuickCreateResult } from 'src/app/shared/crm-quick-create/crm-quick-create-dialog.component';

type PartySourceMode = 'CRM' | 'MANUAL';

@Component({
    selector: 'app-add-edit-sales-order',
    templateUrl: './add-edit-sales-order.component.html',
    styleUrls: ['./add-edit-sales-order.component.css'],
    standalone: true,
    imports: [
        LogisticsProjectLookupComponent,
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        RouterModule,
        ToastModule,
        InputTextModule,
        ButtonModule,
        ToolbarModule,
        CardModule,
        InputNumberModule,
        TextareaModule,
        SelectModule,
        BreadcrumbModule,
        TableModule,
        DatePickerModule,
        ProgressBarModule,
        DividerModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        TooltipModule,
        ConfirmDialogModule,
        EditorModule,
        FlexFieldFormComponent,
        CustomerFormComponent,
        CompanyFormComponent,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent,
        PageMetricStripComponent,
        CommercialPartyPickerComponent,
        LazyLocationPickerDirective
    ],
    providers: [
        MessageService,
        OrdersService,
        ProductsService,
        CustomerService,
        CrmService,
        ContractsService,
        ConfirmationService,
        DialogService
    ]
})
export class AddEditSalesOrderComponent implements OnInit {

    commercialPartyReference: CommercialPartyReference = { source: 'CRM' };

    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = {};

    salesOrderForm!: FormGroup;
    priceTargetLine: AbstractControl | null = null;
    targetNetAmount: number | null = null;
    editMode = false;
    isLocked = false;
    lockReason = '';
    salesOrderId!: number;
    isLoading: boolean = false;
    isAiParsing = false;
    aiExtraction: AiParsedOrderDocument | null = null;
    aiExtractionUnmatchedProducts = 0;
    aiExtractionCounterpartyMatched = false;
    submitted: boolean = false;
    partySourceMode: PartySourceMode = 'CRM';

    companies: SelectItem<Company>[] = [];
    customers: SelectItem<Customer>[] = [];
    mainParties: SelectItem<Party>[] = [];
    subParties: SelectItem<Party>[] = [];
    crmClients: SelectItem<CrmClient>[] = [];
    paymentTerms: SelectItem<PaymentTerm>[] = [];
    products: SelectItem<Products>[] = [];
    productVariants: SelectItem<ProductVariant>[] = [];
    salespersons: SelectItem<Customer>[] = [];

    currentOwningCompanyId?: number;
    currentCountry?: Country;
    currentSalesOrder?: SalesOrder;
    executionLineage?: ExecutionOrderLineage;
    headerFlexFieldValues: FlexFieldValue[] = [];
    lineFlexFieldValues: FlexFieldValue[][] = [];
    subtotal: number = 0;
   tax: number = 0;
   total: number = 0;
   currencyCode: string = 'SAR'; // Default currency
    showCompanyForm = false;
    companyFormHeader = 'Create Company';
    companyFormSubmitLabel = 'Create';
    showCustomerForm = false;
    customerFormHeader = 'Create Customer';
    customerFormSubmitLabel = 'Create';
    selectedCompany: Company = new Company();
    selectedCustomer: Customer = new Customer();
    crmClientContacts: CrmClientCommunication[] = [];
    private pendingAiContact?: { name?: string | null; email?: string | null; phone?: string | null };
    private pendingAiSourceDocument?: File;
    quickMappingLine: number | null = null;
    private productDialogRef?: DynamicDialogRef;

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private location: Location, private router: Router,
        private partyService: PartyService,
        private messageService: MessageService,
        private salesOrderService: OrdersService,
        private productsService: ProductsService,
        private customerService: CustomerService,
        private crmService: CrmService,
        private enterpriseFeatureService: EnterpriseFeatureService,
        private sessionStorageService: SessionStorageService,
        private logisticsProjectsService: LogisticsProjectsService,
        private confirmationService: ConfirmationService,
        private orderDocumentAiService: OrderDocumentAiService,
        private firebaseStorageService: FirebaseStorageService,
        private dialogService: DialogService
    ) {
    }

    ngOnInit(): void {
        this.breadcrumbItems = [
            { label: 'Sales Order', routerLink: '/order/sales-order' },
            { label: this.editMode ? 'Edit Sales Order' : 'Add Sales Order' }
        ];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        const owningCompany = this.sessionStorageService.getCompany();
        if (owningCompany) {
            this.currentOwningCompanyId = owningCompany.id;
        }
        this.currentCountry = this.sessionStorageService.getCountryList()
            ?.find(country => country.id === owningCompany?.countryId);
        this.currencyCode = this.normalizeCurrencyCode(this.currentCountry?.currencyCode);

        this.initForm();
        this.applyPartyValidators();

        this.route.params.subscribe(params => {
            this.salesOrderId = params['id'];
            this.editMode = !!this.salesOrderId;
            this.loadData();
        });

        if (!this.enterpriseFeatureService.isLoaded()) {
            this.enterpriseFeatureService.getAllSubscriptions().subscribe(() => {
                this.applyPartyValidators();
                this.loadPartyOptionsForMode();
            });
        } else {
            this.loadPartyOptionsForMode();
        }
        this.loadProductsByCompany(this.currentOwningCompanyId ?? 0);
    }

    loadData() {
        this.isLoading = true;

        const loadObservables = {
            paymentTerms: this.loadPaymentTerms(),
            companies: this.loadCompanies(),
            salesPersons: this.loadSalesPersons(),
        };

        forkJoin(loadObservables)
            .pipe(
                tap(() => {
                    if (this.editMode) {
                        this.loadSalesOrder().subscribe();
                    }
                })
            )
            .subscribe({
                error: (error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'An error occurred while loading data.' + error.message
                    });
                    //this.cancel();
                    this.isLoading = false;
                },
                complete: () => {
                    this.isLoading = false;
                }
            });
    }

    loadPaymentTerms() {
        const companyId = this.currentOwningCompanyId ?? this.sessionStorageService.getCompany()?.id;
        return this.salesOrderService.getAllPaymentTerms(companyId).pipe(
            tap(paymentTerms => {
                this.paymentTerms = paymentTerms.map((paymentTerm) => ({
                    value: paymentTerm,
                    label: paymentTerm.name
                }));
            })
        );
    }

    loadCompanies() {
        return this.partyService.getParties(this.sessionStorageService.getCompany()?.id ?? 0, ['Company'], '', 0, 1000).pipe(
            tap(value => {
                this.companies = value.content.map(party => this.partyService.convertPartyToCompany(party)).map(company => ({
                    value: company,
                    label: company.name
                }));
            })
        );
    }

    private loadMainParties(): void {
        const companyId = this.sessionStorageService.getCompany()?.id ?? 0;
        if (!companyId) {
            this.mainParties = [];
            return;
        }
        this.partyService.getParties(companyId, [], '', 0, 1000).subscribe(result => {
                this.mainParties = result.content.map(party => ({
                    value: party,
                    label: this.partyLabel(party)
                }));
            });
    }

    onMainPartyChange(event: SelectChangeEvent, preserveSubParty = false): void {
        const party = event.value as Party | null;
        if (!preserveSubParty) {
            this.salesOrderForm.patchValue({ subParty: null });
        }
        if (!party || party.id.entityType !== PartyType.Company) {
            this.subParties = [];
            return;
        }
        const owningCompanyId = this.sessionStorageService.getCompany()?.id ?? 0;
        this.customerService.getCustomersByCompany(party.id.entityId).subscribe(customers => {
            this.subParties = customers.map(customer => {
                const contact = this.partyService.convertToParty(customer, PartyType.Customer, owningCompanyId);
                return { value: contact, label: this.partyLabel(contact) };
            });
        });
    }

    isCompanyMainParty(): boolean {
        return (this.salesOrderForm?.get('mainParty')?.value as Party | null)?.id?.entityType === PartyType.Company;
    }

    private partyLabel(party: Party): string {
        return `${party.entityName || 'Unnamed party'} (${party.id.entityType})`;
    }

    loadCustomersByCompany(event: SelectChangeEvent) {
        if (this.crmActive()) {
            return;
        }
        const company = event.value as Company | null;
        if (!company) {
            this.customers = [];
            this.currentOwningCompanyId = undefined;
            this.headerFlexFieldValues = [];
            this.lineFlexFieldValues = [];
            this.currencyCode = this.defaultCurrencyCode();
            this.salesOrderForm.patchValue({ currencyCode: this.currencyCode }, { emitEvent: false });
            return;
        }
        if (!this.editMode || this.currentSalesOrder?.company?.id !== company.id) {
            this.headerFlexFieldValues = [];
            this.lineFlexFieldValues = [];
        }
        this.crmService.getClients(company.id, ['CUSTOMER']).subscribe(clients => {
            this.customers = clients.map(client => this.crmClientToCustomerOption(client, 'Customer'));
            const selectedCustomer = this.salesOrderForm.get('customer')?.value as Customer | null;
            if (selectedCustomer?.id && !this.customers.some(option => option.value?.id === selectedCustomer.id)) {
                this.customers.push({ value: selectedCustomer, label: selectedCustomer.name });
            }
        });
        if (!this.editMode) {
            this.salesOrderForm.patchValue({ customer: null });
        }
    }

    loadCrmClients() {
        const companyId = this.sessionStorageService.getCompany()?.id ?? this.currentOwningCompanyId ?? 0;
        if (!companyId) {
            this.crmClients = [];
            this.crmClientContacts = [];
            return of([]);
        }
        return this.crmService.getClients(companyId, ['CUSTOMER']).pipe(
            tap(clients => {
                this.crmClients = clients.map(client => ({
                    value: client,
                    label: `${client.companyName || client.notifyingPersonName || 'CRM Client'}${client.clientNumber ? ` (${client.clientNumber})` : ''}`,
                    disabled: !client.linkedCompanyId && !client.linkedCustomerId
                }));
                const selectedClient = this.salesOrderForm.get('crmClient')?.value as CrmClient | null;
                if (selectedClient?.id && !this.crmClients.some(option => option.value?.id === selectedClient.id)) {
                    this.crmClients.push({
                        value: selectedClient,
                        label: `${selectedClient.companyName || selectedClient.notifyingPersonName || 'CRM Client'}${selectedClient.clientNumber ? ` (${selectedClient.clientNumber})` : ''}`
                    });
                }
            })
        );
    }

    onCrmClientChange(event: SelectChangeEvent) {
        const crmClient = event.value as CrmClient | null;
        this.partySourceMode = 'CRM';
        this.applyPartyValidators();
        if (!crmClient) {
            this.crmClientContacts = [];
            this.salesOrderForm.patchValue({ mainParty: null, subParty: null, company: null, customer: null, crmClientContactId: null });
            this.refreshClientItemCodes();
            return;
        }
        const defaultPaymentTerm = this.paymentTerms
            .map(option => option.value)
            .find(term => term?.paymentTermID === crmClient.defaultPaymentTermId) ?? null;
        this.salesOrderForm.patchValue({
            mainParty: null,
            subParty: null,
            company: null,
            customer: null,
            crmClientContactId: null,
            paymentTerm: defaultPaymentTerm || this.salesOrderForm.get('paymentTerm')?.value,
            currencyCode: crmClient.currencyCode || this.salesOrderForm.get('currencyCode')?.value
        });
        this.loadCrmClientContacts(crmClient);
        this.refreshClientItemCodes();
    }

    onCrmClientPicked(crmClient: CrmClient | null): void {
        this.salesOrderForm.patchValue({ crmClient });
        this.onCrmClientChange({ value: crmClient } as SelectChangeEvent);
    }

    onCrmContactPicked(contact: CrmClientCommunication | null): void {
        this.salesOrderForm.patchValue({ crmClientContactId: contact?.id ?? null });
    }

    get commercialPartyOwningCompanyId(): number | undefined {
        return this.sessionStorageService.getCompany()?.id ?? this.currentOwningCompanyId;
    }

    onCrmQuickCreated(result: CrmQuickCreateResult): void {
        if (result.mode === 'client') {
            const client = result.record;
            this.crmClients = [...this.crmClients, {
                value: client,
                label: `${client.companyName}${client.clientNumber ? ` (${client.clientNumber})` : ''}`,
                disabled: !client.linkedCompanyId && !client.linkedCustomerId
            }];
        }
    }

    onCommercialPartyChange(reference: CommercialPartyReference): void {
        this.commercialPartyReference = reference;
        if (reference.source === 'CRM') {
            this.setPartySourceMode('CRM');
            const crmClient = this.crmClients.find(option => option.value?.id === reference.crmClientId)?.value
                ?? (reference.crmClientId ? ({ id: reference.crmClientId } as CrmClient) : null);
            this.salesOrderForm.patchValue({ crmClient });
            this.onCrmClientChange({ value: crmClient } as SelectChangeEvent);
            this.salesOrderForm.patchValue({
                crmAccountId: reference.crmAccountId ?? null,
                crmClientContactId: reference.crmContactId ?? null
            });
            return;
        }
        this.setPartySourceMode('MANUAL');
        const mainType = reference.legacyCompanyId ? PartyType.Company : PartyType.Customer;
        const mainId = reference.legacyCompanyId ?? reference.legacyCustomerId;
        const mainParty = this.mainParties.find(option =>
            option.value?.id.entityType === mainType && option.value.id.entityId === mainId)?.value ?? null;
        const subParty = reference.legacyCompanyId && reference.legacyCustomerId
            ? ({ id: { entityId: reference.legacyCustomerId, entityType: PartyType.Customer }, entityName: '', isCompany: false,
                owningCompanyId: this.commercialPartyOwningCompanyId } as Party)
            : null;
        this.salesOrderForm.patchValue({ mainParty, subParty, crmAccountId: null, crmClientContactId: null });
        this.applyPartyValidators();
    }

    get crmClientValues(): CrmClient[] {
        return this.crmClients.map(option => option.value).filter((client): client is CrmClient => !!client);
    }

    get selectedCrmContact(): CrmClientCommunication | null {
        const selectedId = Number(this.salesOrderForm?.get('crmClientContactId')?.value || 0);
        return this.crmClientContacts.find(contact => contact.id === selectedId) ?? null;
    }

    get selectedCreditClient(): CrmClient | null {
        return (this.salesOrderForm?.get('crmClient')?.value as CrmClient | null) ?? null;
    }

    paymentTermExceedsCreditPeriod(): boolean {
        const approvedDays = this.selectedCreditClient?.creditPeriodDays;
        const selectedDays = this.salesOrderForm?.get('paymentTerm')?.value?.days;
        return approvedDays !== null && approvedDays !== undefined
            && selectedDays !== null && selectedDays !== undefined
            && selectedDays > approvedDays;
    }

    creditCheckModeLabel(mode?: string): string {
        switch ((mode || 'ENFORCE').toUpperCase()) {
            case 'WARN': return 'Warn Only';
            case 'DISABLED': return 'Disabled';
            default: return 'Enforce and Block';
        }
    }

    loadCrmClientContacts(crmClient: CrmClient | null): void {
        if (!crmClient?.id) {
            this.crmClientContacts = [];
            this.salesOrderForm.patchValue({ crmClientContactId: null }, { emitEvent: false });
            return;
        }
        this.crmService.getClientMasterCommunications(crmClient.id).subscribe(contacts => {
            this.crmClientContacts = contacts ?? [];
            const parsedContact = this.findAiCrmContact(this.crmClientContacts);
            this.salesOrderForm.patchValue({
                crmClientContactId: parsedContact?.id || this.resolvePreferredCrmContactId(this.salesOrderForm.get('crmClientContactId')?.value)
            }, { emitEvent: false });
            this.pendingAiContact = undefined;
        });
    }

    private findAiCrmContact(contacts: CrmClientCommunication[]): CrmClientCommunication | undefined {
        if (!this.pendingAiContact) {
            return undefined;
        }
        return contacts.find(contact => this.matchesAiText(this.pendingAiContact?.email, contact.email)
            || this.matchesAiText(this.pendingAiContact?.phone, contact.phone, contact.mobile)
            || this.matchesAiText(this.pendingAiContact?.name, contact.name));
    }

    loadSalesPersons() {
        const companyId = this.currentOwningCompanyId ?? this.sessionStorageService.getCompany()?.id ?? 0;
        return this.customerService.getCustomersByCompany(companyId).pipe(
            tap(customers => {
                this.salespersons = customers.map(customer => ({
                    value: customer,
                    label: customer.name
                }));
            })
        );
    }

    loadProductsByCompany(companyId: number) {
        this.productsService.getSharedCatalog(companyId).subscribe(products => {
            this.products = products.map((product) => ({
                value: product,
                label: product.name
            }));
            this.hydrateLineProductSelections();
        });
    }

    onProductChange(event: SelectChangeEvent, index: number) {
        if (event.value) {
            const selectedProduct = event.value as Products;
            const lineGroup = this.getSalesOrderLineControls.at(index);
            this.productVariants = [];
            if (selectedProduct.productVariants && selectedProduct.productVariants.length > 0) {
                this.syncVariantControl(lineGroup as FormGroup);
                this.loadProductVariants(selectedProduct).subscribe(
                    () => {
                        lineGroup.patchValue({ description: selectedProduct.description, variant: null, unitPrice: 0 });
                        this.applyInventoryProfile(lineGroup as FormGroup, selectedProduct, null);
                        this.syncClientItemCode(lineGroup as FormGroup, selectedProduct);
                    }
                );
            } else {
                this.syncVariantControl(lineGroup as FormGroup);
                lineGroup.patchValue({
                    description: selectedProduct.description,
                    variant: null,
                    unitPrice: this.resolveSalesUnitPrice(selectedProduct)
                });
                this.applyInventoryProfile(lineGroup as FormGroup, selectedProduct, null);
                this.syncClientItemCode(lineGroup as FormGroup, selectedProduct);
            }
        }
    }

    loadProductVariants(product: Products) {
        return of(product.productVariants).pipe(
            tap(productVariants => {
                this.productVariants = productVariants.map(variant => ({
                    value: variant,
                    label: variant.name
                }));
            })
        )
    }

    onProductVariantChange(event: SelectChangeEvent, index: number) {
        if (event.value) {
            const selectedProductVariant = event.value as ProductVariant;
            const lineGroup = this.getSalesOrderLineControls.at(index);
            const selectedProduct = lineGroup.get('product')?.value as Products | null;
            lineGroup.patchValue({ unitPrice: this.resolveSalesUnitPrice(selectedProduct, selectedProductVariant) });
            if (selectedProduct) this.applyInventoryProfile(lineGroup as FormGroup, selectedProduct, selectedProductVariant);
        }
    }

    private applyInventoryProfile(lineGroup: FormGroup, product: Products, variant: ProductVariant | null): void {
        const profile = product.inventoryProfiles?.find(row => row.variantId === variant?.variantID)
            || (!variant ? product.inventoryProfiles?.find(row => row.variantId == null) : undefined);
        if (!product.inventoryManaged || !profile) {
            lineGroup.patchValue({ inventoryItemId: null, sourceWarehouseId: null, conditionId: null,
                orderUomId: null, baseUomId: null, conversionFactorToBase: 1, orderedBaseQuantity: null }, { emitEvent: false });
            return;
        }
        const orderUomId = profile.defaultSalesUomId || profile.baseUomId;
        const conversion = profile.uomConversions?.find(row => row.uomId === orderUomId
            && (row.usageType === 'SALES' || row.usageType === 'BOTH'));
        const factor = orderUomId === profile.baseUomId ? 1 : Number(conversion?.factorToBase || 1);
        const quantity = Number(lineGroup.get('quantity')?.value || 0);
        lineGroup.patchValue({ inventoryItemId: profile.warehouseItemId,
            sourceWarehouseId: profile.defaultWarehouseId, conditionId: profile.defaultConditionId,
            orderUomId, baseUomId: profile.baseUomId, conversionFactorToBase: factor,
            orderedBaseQuantity: quantity * factor, allocationStatus: 'NOT_ALLOCATED' }, { emitEvent: false });
    }

    loadSalesOrder() {
        return this.salesOrderService.getSalesOrderById(this.salesOrderId).pipe(
            tap(salesOrder => {
                this.currentSalesOrder = salesOrder;
                this.headerFlexFieldValues = salesOrder.flexFieldValues ?? [];
                this.lineFlexFieldValues = (salesOrder.salesOrderLines ?? []).map(line => line.flexFieldValues ?? []);
                this.patchSalesOrderForm(salesOrder);
                const state = (salesOrder.state || '').toUpperCase();
                const billingStatus = (salesOrder.billingStatus || '').toUpperCase();
                const invoiceGenerated = !!salesOrder.invoiceGenerated || state === 'INVOICED'
                    || ['INVOICED', 'PARTIALLY_INVOICED', 'INVOICE_DRAFTED', 'BILLED'].includes(billingStatus);
                if (invoiceGenerated) {
                    this.isLocked = true;
                    this.lockReason = 'This sales order cannot be edited because a customer invoice has already been generated.';
                    this.salesOrderForm.disable();
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Order Locked',
                        detail: this.lockReason
                    });
                }
                const owningCompanyId = salesOrder.owningCompanyId ?? salesOrder.company?.id;
                if (owningCompanyId) {
                    if (this.currentOwningCompanyId !== owningCompanyId) {
                        this.currentOwningCompanyId = owningCompanyId;
                        this.loadProductsByCompany(owningCompanyId);
                    }
                    this.salesOrderService.getExecutionLineage(this.salesOrderId, owningCompanyId).subscribe({
                        next: lineage => this.executionLineage = lineage,
                        error: () => this.executionLineage = undefined
                    });
                }
                salesOrder.salesOrderLines?.forEach(line => {
                    if (line.product && line.product.productVariants && line.product.productVariants.length > 0) {
                        this.loadProductVariants(line.product).subscribe();
                    }
                });
            })
        );
    }

    private patchSalesOrderForm(salesOrder: SalesOrder): void {
        const orderDate = salesOrder.orderDate ? new Date(salesOrder.orderDate) : null;
        const expectedDate = salesOrder.expectedDate ? new Date(salesOrder.expectedDate) : null;
        const deliveryWindowStart = salesOrder.deliveryWindowStart ? new Date(salesOrder.deliveryWindowStart) : null;
        const deliveryWindowEnd = salesOrder.deliveryWindowEnd ? new Date(salesOrder.deliveryWindowEnd) : null;
        this.partySourceMode = salesOrder.crmClient?.id || salesOrder.crmClientId ? 'CRM' : 'MANUAL';
        this.applyPartyValidators();
        let matchedSalesperson: Customer | null = null;
        if (salesOrder.salesperson) {
            const found = this.salespersons.find(opt => opt.value?.id === salesOrder.salesperson.id);
            if (found) {
                matchedSalesperson = found.value;
            } else {
                matchedSalesperson = salesOrder.salesperson;
                this.salespersons.push({ value: matchedSalesperson, label: matchedSalesperson.name });
            }
        }

        let matchedPaymentTerm: PaymentTerm | null = null;
        if (salesOrder.paymentTerm) {
            const found = this.paymentTerms.find(opt => opt.value?.paymentTermID === salesOrder.paymentTerm.paymentTermID);
            if (found) {
                matchedPaymentTerm = found.value;
            } else {
                matchedPaymentTerm = salesOrder.paymentTerm;
                this.paymentTerms.push({ value: matchedPaymentTerm, label: matchedPaymentTerm.name });
            }
        }

        this.salesOrderForm.patchValue({
            orderID: salesOrder.orderID ?? null,
            orderNumber: salesOrder.orderNumber ?? '',
            externalReferenceNumber: salesOrder.externalReferenceNumber ?? '',
            crmClient: salesOrder.crmClient ?? null,
            crmAccountId: salesOrder.crmAccountId ?? null,
            crmClientContactId: salesOrder.crmClientContactId ?? salesOrder.crmClientContact?.id ?? null,
            mainParty: salesOrder.mainParty ?? null,
            subParty: salesOrder.subParty ?? null,
            company: salesOrder.company ?? null,
            customer: salesOrder.customer ?? null,
            salesperson: matchedSalesperson,
            paymentTerm: matchedPaymentTerm,
            orderDate,
            expectedDate,
            shipToLocationId: salesOrder.shipToLocationId ?? null,
            deliveryWindowStart,
            deliveryWindowEnd,
            logisticsProjectId: salesOrder.logisticsProjectId ?? null,
            currencyCode: this.normalizeCurrencyCode(salesOrder.currencyCode ?? this.currencyCode),
            exchangeRate: salesOrder.exchangeRate ?? 1,
            notes: salesOrder.notes ?? ''
        });
        this.currencyCode = this.normalizeCurrencyCode(salesOrder.currencyCode || this.currencyCode);
        this.commercialPartyReference = {
            source: salesOrder.crmClient?.id || salesOrder.crmClientId ? 'CRM' : 'LEGACY',
            crmClientId: salesOrder.crmClient?.id ?? salesOrder.crmClientId,
            crmAccountId: salesOrder.crmAccountId,
            crmContactId: salesOrder.crmClientContactId ?? salesOrder.crmClientContact?.id,
            legacyCompanyId: salesOrder.mainParty?.id?.entityType === PartyType.Company ? salesOrder.mainParty.id.entityId : undefined,
            legacyCustomerId: salesOrder.subParty?.id?.entityId
                ?? (salesOrder.mainParty?.id?.entityType === PartyType.Customer ? salesOrder.mainParty.id.entityId : undefined)
        };
        this.crmClientContacts = salesOrder.crmClientContacts ?? [];

        if (salesOrder.crmClient) {
            this.loadCrmClientContacts(salesOrder.crmClient);
            this.loadCrmClients().subscribe();
        } else if (salesOrder.company && this.isManualPartySource()) {
            this.loadCustomersByCompany({ value: salesOrder.company } as SelectChangeEvent);
        }
        if (salesOrder.mainParty && this.isManualPartySource()) {
            this.onMainPartyChange({ value: salesOrder.mainParty } as SelectChangeEvent, true);
        }

        const lines = salesOrder.salesOrderLines?.length ? salesOrder.salesOrderLines : [];
        const lineControls = this.fb.array(lines.map(line => this.createSalesOrderLineFormGroup(line)));
        if (!lines.length) {
            lineControls.push(this.createSalesOrderLineFormGroup());
        }
        this.salesOrderForm.setControl('salesOrderLines', lineControls);
        this.hydrateLineProductSelections();
        this.refreshClientItemCodes();
        this.getSalesOrderLineControls.valueChanges.subscribe(() => {
            this.calculateTotals();
        });
        this.calculateTotals();
    }

    private extractFlexFieldValues(array: FormArray | null): FlexFieldValue[] {
        if (!array) {
            return [];
        }
        return array.controls.map(control => control.value as FlexFieldValue);
    }

    initForm() {
        this.salesOrderForm = this.fb.group({
            orderID: [null],
            orderNumber: [''],
            externalReferenceNumber: [''],
            crmClient: [null],
            crmAccountId: [null],
            crmClientContactId: [null],
            mainParty: [null],
            subParty: [null],
            company: [null],
            customer: [null],
            salesperson: [null],
            paymentTerm: [null, Validators.required],
            orderDate: [new Date(), Validators.required],
            expectedDate: [new Date(), Validators.required],
            shipToLocationId: [null],
            deliveryWindowStart: [null],
            deliveryWindowEnd: [null],
            logisticsProjectId: [Number(this.route.snapshot.queryParamMap.get('projectId') || '') || null],
            currencyCode: [this.normalizeCurrencyCode(this.currencyCode)],
            exchangeRate: [1],
            notes: [''],
            flexFields: this.fb.array([]),
            salesOrderLines: this.fb.array([this.createSalesOrderLineFormGroup()]),
        });
        this.headerFlexFieldValues = [];
        this.lineFlexFieldValues = [];

        // Add this subscription
        this.getSalesOrderLineControls.valueChanges.subscribe(() => {
            this.calculateTotals();
        });
        this.salesOrderForm.get('currencyCode')?.valueChanges.subscribe(value => {
            this.currencyCode = this.normalizeCurrencyCode(value);
            this.calculateTotals();
        });
    }

    createSalesOrderLineFormGroup(salesOrderLine?: SalesOrderLine): FormGroup {
        const formGroup = this.fb.group({
            lineID: [salesOrderLine?.lineID ?? null],
            orderID: [salesOrderLine?.orderID ?? null],
            lineNumber: [salesOrderLine?.lineNumber ?? null],
            product: [salesOrderLine?.product ?? null, Validators.required],
            variant: [salesOrderLine?.variant ?? null, Validators.required],
            description: [salesOrderLine?.description ?? ''],
            quantity: [salesOrderLine?.quantity ?? 1, [Validators.required, Validators.min(1)]],
            unitPrice: [salesOrderLine?.unitPrice ?? 0, [Validators.required, Validators.min(0)]],
            displayUnitPrice: [Number(Number(salesOrderLine?.unitPrice ?? 0).toFixed(4))],
            appliedLineDiscount: [salesOrderLine?.appliedLineDiscount ?? 0],
            sourceQuotationLineId: [salesOrderLine?.sourceQuotationLineId ?? null],
            priceBasis: [salesOrderLine?.priceBasis ?? null],
            priceSource: [salesOrderLine?.priceSource ?? null],
            priceOverrideReason: [salesOrderLine?.priceOverrideReason ?? ''],
            crmClientItemCode: [''],
            crmClientItemMappingId: [null],
            logisticsProjectId: [salesOrderLine?.logisticsProjectId ?? null],
            inventoryItemId: [salesOrderLine?.inventoryItemId ?? null],
            sourceWarehouseId: [salesOrderLine?.sourceWarehouseId ?? null],
            conditionId: [salesOrderLine?.conditionId ?? null],
            orderUomId: [salesOrderLine?.orderUomId ?? null],
            baseUomId: [salesOrderLine?.baseUomId ?? null],
            conversionFactorToBase: [salesOrderLine?.conversionFactorToBase ?? 1],
            orderedBaseQuantity: [salesOrderLine?.orderedBaseQuantity ?? null],
            allocatedBaseQuantity: [salesOrderLine?.allocatedBaseQuantity ?? 0],
            releasedBaseQuantity: [salesOrderLine?.releasedBaseQuantity ?? 0],
            deliveredBaseQuantity: [salesOrderLine?.deliveredBaseQuantity ?? 0],
            allocationStatus: [salesOrderLine?.allocationStatus ?? 'NOT_ALLOCATED'],
            flexFields: this.fb.array([]),
        });

        // PrimeNG emits the four-decimal display value on blur; keep the saved rate separate.
        let displayedPrice = formGroup.get('displayUnitPrice')?.value;
        formGroup.get('unitPrice')?.valueChanges.subscribe(price => {
            displayedPrice = price == null ? null : Number(Number(price).toFixed(4));
            formGroup.get('displayUnitPrice')?.setValue(displayedPrice, { emitEvent: false });
        });
        formGroup.get('displayUnitPrice')?.valueChanges.subscribe(price => {
            if (price !== displayedPrice) formGroup.get('unitPrice')?.setValue(price);
        });
        this.syncVariantControl(formGroup);

        return formGroup;
    }

    productVariantsFor(lineGroup: AbstractControl): SelectItem<ProductVariant>[] {
        const product = lineGroup.get('product')?.value as Products | null;
        const selectedVariant = lineGroup.get('variant')?.value as ProductVariant | null;
        const variants = product?.productVariants ?? [];
        const options = selectedVariant && !variants.some(variant => variant.variantID === selectedVariant.variantID)
            ? [selectedVariant, ...variants]
            : variants;
        return options.map(variant => ({
            value: variant,
            label: variant.name
        }));
    }

    private hydrateLineProductSelections(): void {
        if (!this.salesOrderForm) {
            return;
        }
        this.getSalesOrderLineControls.controls.forEach(control => {
            const lineGroup = control as FormGroup;
            const requestedProduct = lineGroup.get('product')?.value as Products | null;
            const product = this.catalogProduct(requestedProduct);
            const requestedVariant = lineGroup.get('variant')?.value as ProductVariant | null;
            const variant = this.catalogVariant(product, requestedVariant);

            lineGroup.patchValue({ product, variant }, { emitEvent: false });
            this.syncVariantControl(lineGroup);
            this.syncClientItemCode(lineGroup, product);
        });
    }

    private catalogProduct(product: Products | null | undefined): Products | null {
        if (!product?.productID) {
            return product ?? null;
        }
        const catalogProduct = this.products.find(option => option.value.productID === product.productID)?.value;
        if (catalogProduct) {
            return catalogProduct;
        }
        this.products = [...this.products, { value: product, label: product.name }];
        return product;
    }

    private catalogVariant(product: Products | null, variant: ProductVariant | null | undefined): ProductVariant | null {
        if (!variant?.variantID) {
            return variant ?? null;
        }
        return product?.productVariants?.find(candidate => candidate.variantID === variant.variantID) ?? variant;
    }

    quickMapClientItemCode(index: number): void {
        const lineGroup = this.getSalesOrderLineControls.at(index) as FormGroup;
        const product = lineGroup.get('product')?.value as Products | null;
        const crmClientId = this.selectedCrmClientId();
        const code = String(lineGroup.get('crmClientItemCode')?.value || '').trim();
        if (!product?.productID || !crmClientId || !code) {
            this.messageService.add({ severity: 'warn', summary: 'Client item code', detail: 'Select a CRM client and product, then enter the client item number.' });
            return;
        }
        this.quickMappingLine = index;
        const mappingId = Number(lineGroup.get('crmClientItemMappingId')?.value || 0);
        const userId = Number(this.sessionStorageService.getCustomer()?.id || this.sessionStorageService.getUser()?.id || 0);
        this.productsService.getProductById(product.productID).pipe(
            switchMap(fullProduct => {
                const mappings = [...(fullProduct.crmClientItemCodes || [])];
                const existingIndex = mappings.findIndex(mapping => Number(mapping.id) === mappingId
                    || (!mappingId && Number(mapping.crmClientId) === crmClientId));
                const mapping = { id: mappingId || undefined, crmClientId, clientItemCode: code, active: true };
                if (existingIndex >= 0) mappings[existingIndex] = { ...mappings[existingIndex], ...mapping };
                else mappings.push(mapping);
                fullProduct.crmClientItemCodes = mappings;
                fullProduct.updatedAt = new Date();
                fullProduct.updatedBy = userId || fullProduct.updatedBy;
                return this.productsService.updateProduct(product.productID, fullProduct);
            })
        ).subscribe({
            next: savedProduct => {
                Object.assign(product, savedProduct);
                lineGroup.patchValue({ product });
                this.syncClientItemCode(lineGroup, product);
                this.messageService.add({ severity: 'success', summary: 'Client item code', detail: 'Product mapping saved for the selected CRM client.' });
                this.quickMappingLine = null;
            },
            error: error => {
                this.quickMappingLine = null;
                this.messageService.add({ severity: 'error', summary: 'Client item code', detail: this.extractErrorDetail(error, 'Unable to save the product mapping.') });
            }
        });
    }

    private refreshClientItemCodes(): void {
        if (!this.salesOrderForm) return;
        this.getSalesOrderLineControls.controls.forEach(control => this.syncClientItemCode(control as FormGroup));
    }

    private syncClientItemCode(lineGroup: FormGroup, selectedProduct?: Products | null): void {
        const product = selectedProduct || lineGroup.get('product')?.value as Products | null;
        const crmClientId = this.selectedCrmClientId();
        const mapping = product?.crmClientItemCodes?.find(item => item.active !== false && Number(item.crmClientId) === crmClientId);
        lineGroup.patchValue({ crmClientItemCode: mapping?.clientItemCode || '', crmClientItemMappingId: mapping?.id || null }, { emitEvent: false });
    }

    private selectedCrmClientId(): number {
        return Number((this.salesOrderForm?.get('crmClient')?.value as CrmClient | null)?.id || 0);
    }

    get getSalesOrderLineControls(): FormArray {
        return this.salesOrderForm.get('salesOrderLines') as FormArray;
    }

    get headerFlexFieldControls(): FormArray {
        return this.salesOrderForm.get('flexFields') as FormArray;
    }

    getLineFlexFieldControls(index: number): FormArray {
        const control = this.getSalesOrderLineControls.at(index) as FormGroup;
        return control.get('flexFields') as FormArray;
    }

    formMetrics(): PageMetric[] {
        return [
            { label: 'Order Total', value: this.formatMoney(this.total), hint: this.currencyCode, tone: 'success' },
            { label: 'Lines', value: this.getSalesOrderLineControls.length, hint: 'Order lines', tone: 'info' },
            { label: 'Tax', value: this.formatMoney(this.tax), hint: 'Estimated from product tax' },
            { label: 'Expected', value: this.dateLabel(this.salesOrderForm?.get('expectedDate')?.value), hint: this.salesOrderForm?.get('paymentTerm')?.value?.name || 'No payment term', tone: 'warn' }
        ];
    }

    readinessPercent(): number {
        if (!this.salesOrderForm) {
            return 0;
        }
        const checks = this.readinessItems();
        const complete = checks.filter(item => item.done).length;
        return Math.round((complete / checks.length) * 100);
    }

    readinessItems(): { label: string; done: boolean; hint: string }[] {
        if (this.isCrmPartySource()) {
            const client = this.salesOrderForm?.get('crmClient')?.value as CrmClient | null;
            return [
                { label: 'CRM Client', done: !!client?.id, hint: client?.companyName || client?.notifyingPersonName || 'Required' },
                { label: 'CRM Contact', done: true, hint: this.selectedCrmContactLabel() || 'Optional' },
                { label: 'Terms', done: !!this.salesOrderForm?.get('paymentTerm')?.value, hint: this.salesOrderForm?.get('paymentTerm')?.value?.name || 'Required' },
                { label: 'Dates', done: !!this.salesOrderForm?.get('orderDate')?.valid && !!this.salesOrderForm?.get('expectedDate')?.valid, hint: this.dateLabel(this.salesOrderForm?.get('expectedDate')?.value) },
                { label: 'Lines', done: this.getSalesOrderLineControls.length > 0 && this.getSalesOrderLineControls.valid, hint: `${this.getSalesOrderLineControls.length} line(s)` }
            ];
        }
        return [
            { label: 'Main Party', done: !!this.salesOrderForm?.get('mainParty')?.value, hint: this.salesOrderForm?.get('mainParty')?.value?.entityName || 'Required' },
            { label: 'Sub Party', done: !!this.salesOrderForm?.get('subParty')?.value || !this.isCompanyMainParty(), hint: this.salesOrderForm?.get('subParty')?.value?.entityName || 'Optional contact' },
            { label: 'Terms', done: !!this.salesOrderForm?.get('paymentTerm')?.value, hint: this.salesOrderForm?.get('paymentTerm')?.value?.name || 'Required' },
            { label: 'Dates', done: !!this.salesOrderForm?.get('orderDate')?.valid && !!this.salesOrderForm?.get('expectedDate')?.valid, hint: this.dateLabel(this.salesOrderForm?.get('expectedDate')?.value) },
            { label: 'Lines', done: this.getSalesOrderLineControls.length > 0 && this.getSalesOrderLineControls.valid, hint: `${this.getSalesOrderLineControls.length} line(s)` }
        ];
    }

    formatMoney(amount: number | undefined | null): string {
        return amount !== undefined && amount !== null
            ? `${this.normalizeCurrencyCode(this.currencyCode)} ${Number(amount).toFixed(currencyFractionDigits(this.currencyCode))}`
            : '-';
    }

    private dateLabel(value: Date | string | null | undefined): string {
        return value ? new Date(value).toLocaleDateString() : '-';
    }

    addSalesOrderLine(): void {
        const lineGroup = this.createSalesOrderLineFormGroup();
        this.getSalesOrderLineControls.push(lineGroup);
        this.lineFlexFieldValues.push([]);
    }

    removeSalesOrderLine(index: number): void {
        this.getSalesOrderLineControls.removeAt(index);
        if (index >= 0 && index < this.lineFlexFieldValues.length) {
            this.lineFlexFieldValues.splice(index, 1);
        }
    }

    openReleasedQuantityCorrection(lineId: number): void {
        if (!this.salesOrderId || !lineId) return;
        this.router.navigate(['/order/sales-order/view', this.salesOrderId],
            { queryParams: { correctionLineId: lineId } });
    }

    saveSalesOrder(): void {
        if (this.isLocked) {
            this.messageService.add({
                severity: 'error',
                summary: 'Order Locked',
                detail: this.lockReason || 'This sales order is locked from editing because a customer invoice has been generated.'
            });
            return;
        }
        this.submitted = true;
        if (this.salesOrderForm.invalid) {
            this.salesOrderForm.markAllAsTouched();
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please correct required header fields and line errors before saving.'
            });
            return;
        }

        const selectedMainParty = this.salesOrderForm.get('mainParty')?.value as Party | null;
        const selectedCrmClient = this.salesOrderForm.get('crmClient')?.value as CrmClient | null;
        if (this.isCrmPartySource() && !selectedCrmClient?.id) {
            this.messageService.add({ severity: 'error', summary: 'CRM client required', detail: 'Select a CRM client for this sales order.' });
            return;
        }
        if (this.isManualPartySource() && !selectedMainParty?.id?.entityId) {
            this.messageService.add({
                severity: 'error',
                summary: 'Main party required',
                detail: 'Select the party for this sales order.'
            });
            return;
        }

        if (this.getSalesOrderLineControls.length === 0) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please add at least one sales order line' });
            return;
        }

        if (this.hasMissingSourcePriceOverrideReason()) {
            this.messageService.add({ severity: 'error', summary: 'Price override required', detail: 'Source quotation line price changes require a price override reason.' });
            return;
        }

        this.isLoading = true;
        this.salesOrderForm.disable();
        this.calculateTotals();

        const salesOrderData = this.prepareSaveData();

        const operation = this.editMode
            ? this.salesOrderService.updateSalesOrder(this.salesOrderId, salesOrderData)
            : this.salesOrderService.createSalesOrder(salesOrderData);

        operation.subscribe({
            next: savedOrder => this.handleSaveSuccess(savedOrder),
            error: (error) => this.handleSaveError(error)
        });
    }

    private prepareSaveData(): SalesOrder {
        const formValue = this.salesOrderForm.getRawValue();
        const salesOrderData: SalesOrder = { ...formValue };
        if (!salesOrderData.orderID) {
            salesOrderData.createdBy = this.sessionStorageService.getCustomer()?.id ?? 0;
            salesOrderData.createdAt = new Date();
            salesOrderData.state = 'DRAFT';
        }
        salesOrderData.state = this.editMode ? (this.currentSalesOrder?.state ?? 'DRAFT') : salesOrderData.state;
        const selectedCrmClient = formValue.crmClient as CrmClient | null;
        if (this.isCrmPartySource()) {
            salesOrderData.crmClient = this.minimalCrmClientRef(selectedCrmClient?.id);
            (salesOrderData as any).crmClientId = selectedCrmClient?.id ?? null;
            salesOrderData.crmAccountId = formValue.crmAccountId ?? null;
            salesOrderData.crmClientContactId = formValue.crmClientContactId ?? null;
            salesOrderData.mainParty = undefined;
            salesOrderData.subParty = undefined;
        } else {
            salesOrderData.crmClient = null as any;
            salesOrderData.crmClientId = null as any;
            salesOrderData.crmAccountId = null as any;
            salesOrderData.crmClientContactId = null as any;
            salesOrderData.mainParty = this.minimalPartyRef(formValue.mainParty);
            salesOrderData.subParty = this.minimalPartyRef(formValue.subParty);
        }
        salesOrderData.salesperson = this.minimalCustomerRef(formValue.salesperson?.id) as Customer;
        salesOrderData.paymentTerm = this.minimalPaymentTermRef(formValue.paymentTerm?.paymentTermID) as PaymentTerm;
        salesOrderData.externalReferenceNumber = formValue.externalReferenceNumber ? formValue.externalReferenceNumber.trim() : null;
        salesOrderData.company = null as any;
        salesOrderData.customer = null as any;
        salesOrderData.approvalStatus = this.currentSalesOrder?.approvalStatus ?? 'NOT_REQUIRED';
        salesOrderData.fulfillmentStatus = this.currentSalesOrder?.fulfillmentStatus ?? 'NOT_STARTED';
        salesOrderData.billingStatus = this.currentSalesOrder?.billingStatus ?? 'NOT_INVOICED';
        salesOrderData.holdStatus = this.currentSalesOrder?.holdStatus ?? 'CLEAR';
        salesOrderData.versionNumber = this.currentSalesOrder?.versionNumber;
        salesOrderData.sourceType = this.currentSalesOrder?.sourceType;
        salesOrderData.sourceQuotationId = this.currentSalesOrder?.sourceQuotationId;
        salesOrderData.sourceQuotationNumber = this.currentSalesOrder?.sourceQuotationNumber;
        salesOrderData.sourceQuotationRevision = this.currentSalesOrder?.sourceQuotationRevision;
        salesOrderData.sourceQuotationAcceptedAt = this.currentSalesOrder?.sourceQuotationAcceptedAt;
        salesOrderData.sourceQuotationAcceptedBy = this.currentSalesOrder?.sourceQuotationAcceptedBy;
        salesOrderData.sourceContractId = this.currentSalesOrder?.sourceContractId;
        salesOrderData.sourceRateCardId = this.currentSalesOrder?.sourceRateCardId;
        salesOrderData.priceBasis = this.currentSalesOrder?.priceBasis;
        salesOrderData.updatedBy = this.sessionStorageService.getCustomer()?.id ?? 0;
        salesOrderData.updatedAt = new Date();
        salesOrderData.owningCompanyId = this.currentOwningCompanyId ?? 0;
        salesOrderData.logisticsProjectId = formValue.logisticsProjectId || undefined;
        salesOrderData.currencyCode = this.normalizeCurrencyCode(formValue.currencyCode || this.currencyCode);
        salesOrderData.exchangeRate = formValue.exchangeRate || 1;

        // Map header flex fields to the expected format
        salesOrderData.flexFieldValues = (formValue.flexFields || []).map((field: any) => ({
            configId: field.configId,
            value: field.value
        }));
        delete (salesOrderData as any).flexFields;

        // Map line item flex fields
        salesOrderData.salesOrderLines?.forEach((line, index) => {
            const lineFormValue = formValue.salesOrderLines[index];
            delete (line as any).crmClientItemCode;
            delete (line as any).crmClientItemMappingId;
            delete (line as any).displayUnitPrice;
            line.flexFieldValues = (lineFormValue.flexFields || []).map((field: any) => ({ configId: field.configId, value: field.value }));
            delete (line as any).flexFields;

            // Calculate financial fields for the line
            const quantity = line.quantity || 0;
            const unitPrice = line.unitPrice || 0;
            const taxRate = (line.product?.taxRate || 0) / 100;
            const amounts = orderLineAmounts(quantity, unitPrice, taxRate * 100, salesOrderData.currencyCode || this.currencyCode, Number(line.appliedLineDiscount || 0));
            line.unitTaxAmount = amounts.tax / (quantity || 1);
            line.unitDiscount = amounts.discount / (quantity || 1);
            line.unitLineAmount = unitPrice;
            line.appliedLineDiscount = amounts.discount;
            line.totalTax = amounts.tax;
            line.totalLineAmount = amounts.total;
            line.taxRate = (line.product?.taxRate || 0);
            line.taxCode = line.taxRate > 0 ? 'VAT' : 'NO_TAX';
            line.taxInclusive = false;
            line.taxJurisdiction = 'SA';
            line.priceSource = line.priceSource || 'MANUAL';
            const existingLine = this.findExistingLine(line);
            if (existingLine?.sourceQuotationLineId) {
                line.sourceQuotationLineId = existingLine.sourceQuotationLineId;
                line.priceBasis = existingLine.priceBasis || 'CRM_QUOTATION';
                line.priceSource = existingLine.priceSource || 'QUOTATION';
                line.manualPriceOverride = Number(line.unitPrice || 0) !== Number(existingLine.unitPrice || 0);
            } else {
                line.manualPriceOverride = false;
            }

            line.lineNumber = (index + 1).toString();
            if (!line.orderID) {
                line.createdBy = this.sessionStorageService.getCustomer()?.id ?? 0;
                line.createdAt = new Date();
            }
            line.updatedBy = this.sessionStorageService.getCustomer()?.id ?? 0;
            line.updatedAt = new Date();
            line.owningCompanyId = this.currentOwningCompanyId ?? 0;
            line.logisticsProjectId = line.logisticsProjectId || salesOrderData.logisticsProjectId;
            line.product = this.minimalProductRef(line.product?.productID) as Products;
            line.variant = this.minimalVariantRef((line as any).variant?.variantID) as any;
        });
        salesOrderData.subtotalAmount = this.subtotal;
        salesOrderData.discountAmount = this.getSalesOrderLineControls.controls.reduce((sum, line) => sum + this.lineAmounts(line).discount, 0);
        salesOrderData.taxAmount = this.tax;
        salesOrderData.totalAmount = this.total;
        salesOrderData.baseCurrencyAmount = this.total * (salesOrderData.exchangeRate || 1);
        return salesOrderData;
    }

    onAiOrderDocumentSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file || this.isAiParsing) {
            return;
        }
        if (file.size > 15 * 1024 * 1024) {
            this.messageService.add({ severity: 'warn', summary: 'File too large', detail: 'Upload a document smaller than 15 MB.' });
            input.value = '';
            return;
        }

        this.aiExtraction = null;
        this.aiExtractionUnmatchedProducts = 0;
        this.aiExtractionCounterpartyMatched = false;
        this.isAiParsing = true;
        this.orderDocumentAiService.extract(file, 'SALES_ORDER').subscribe({
            next: result => {
                this.pendingAiSourceDocument = file;
                const unmatchedProducts = this.applyAiOrderDocument(result);
                this.aiExtraction = result;
                this.aiExtractionUnmatchedProducts = unmatchedProducts;
                const warnings = result.warnings ?? [];
                const detail = unmatchedProducts
                    ? `Order draft populated. ${unmatchedProducts} line item(s) need a product selection.`
                    : 'Order draft populated from the uploaded document. Review the values before saving.';
                this.messageService.add({ severity: warnings.length ? 'warn' : 'success', summary: 'AI document parsed', detail });
                if (warnings.length) {
                    this.messageService.add({ severity: 'warn', summary: 'AI review required', detail: warnings.slice(0, 2).join(' ') });
                }
            },
            error: error => this.messageService.add({
                severity: 'error',
                summary: 'AI parse failed',
                detail: error?.error?.message || 'The document could not be parsed. Try a clear image or text PDF.'
            }),
            complete: () => {
                this.isAiParsing = false;
                input.value = '';
            }
        });
    }

    private applyAiOrderDocument(result: AiParsedOrderDocument): number {
        const header: Record<string, any> = {};
        const orderDate = this.parseAiDate(result.orderDate);
        const expectedDate = this.parseAiDate(result.expectedDate);
        const paymentTerm = this.findPaymentTerm(result.paymentTerms);

        if (orderDate) header['orderDate'] = orderDate;
        if (expectedDate) header['expectedDate'] = expectedDate;
        if (result.currencyCode) header['currencyCode'] = this.normalizeCurrencyCode(result.currencyCode);
        if (paymentTerm) header['paymentTerm'] = paymentTerm;
        if (result.documentNumber || result.notes) {
            header['notes'] = [
                result.documentNumber ? `Source document: ${result.documentNumber}` : '',
                result.notes || ''
            ].filter(Boolean).join('\n');
        }
        this.salesOrderForm.patchValue(header);

        this.aiExtractionCounterpartyMatched = !!(result.buyerName || result.billToName || result.counterpartyName) && this.applyAiCustomer(result);
        if (!this.aiExtractionCounterpartyMatched) {
            this.setPartySourceMode('MANUAL');
        }
        const unmatchedProducts = this.replaceAiSalesOrderLines(result.lines ?? []);
        this.calculateTotals();
        return unmatchedProducts;
    }

    private applyAiCustomer(result: AiParsedOrderDocument): boolean {
        const counterpartyName = result.buyerName || result.billToName || result.counterpartyName || '';
        if (this.crmActive()) {
            const client = this.crmClientValues.find(value => this.matchesAiText(
                counterpartyName,
                value.companyName,
                value.notifyingPersonName,
                value.clientNumber));
            if (client) {
                this.pendingAiContact = {
                    name: result.contactName,
                    email: result.contactEmail,
                    phone: result.contactPhone
                };
                this.onCrmClientPicked(client);
                return true;
            } else {
                this.messageService.add({ severity: 'warn', summary: 'CRM client not matched', detail: `Select the CRM client for "${counterpartyName}" manually.` });
            }
            return false;
        }

        const party = this.mainParties.map(option => option.value)
            .find(value => this.matchesAiText(counterpartyName, value?.entityName));
        if (party) {
            this.salesOrderForm.patchValue({ mainParty: party });
            this.onMainPartyChange({ value: party } as SelectChangeEvent);
            return true;
        } else {
            this.messageService.add({ severity: 'warn', summary: 'Party not matched', detail: `Select the party for "${counterpartyName}" manually.` });
            return false;
        }
    }

    private replaceAiSalesOrderLines(lines: AiParsedOrderLine[]): number {
        if (!lines.length) {
            return 0;
        }
        const lineGroups = lines.map(line => this.createAiSalesOrderLine(line));
        this.salesOrderForm.setControl('salesOrderLines', this.fb.array(lineGroups));
        this.getSalesOrderLineControls.valueChanges.subscribe(() => this.calculateTotals());
        this.lineFlexFieldValues = lineGroups.map(() => []);
        return lineGroups.filter(group => !group.get('product')?.value).length;
    }

    private createAiSalesOrderLine(line: AiParsedOrderLine): FormGroup {
        const product = this.findAiProduct(line);
        const group = this.createSalesOrderLineFormGroup();
        const quantity = this.aiNumber(line.quantity, 1);
        const unitPrice = this.aiNumber(line.unitPrice,
            this.aiDerivedUnitPrice(line, quantity, product ? this.resolveSalesUnitPrice(product) : 0));
        group.patchValue({
            product,
            description: line.description || product?.description || '',
            quantity,
            unitPrice,
            priceSource: 'MANUAL'
        }, { emitEvent: false });
        if (product) {
            this.syncVariantControl(group);
            this.applyInventoryProfile(group, product, null);
        }
        return group;
    }

    private findAiProduct(line: AiParsedOrderLine): Products | null {
        const crmClientId = Number((this.salesOrderForm.get('crmClient')?.value as CrmClient | null)?.id || 0);
        return this.products.map(option => option.value).find(product =>
            product?.crmClientItemCodes?.some(mapping => mapping.active !== false
                && Number(mapping.crmClientId) === crmClientId
                && this.matchesAiText(line.itemCode || line.productCode, mapping.clientItemCode))
            || this.matchesAiText(line.itemCode || line.productCode, product?.productCode)
            || this.matchesAiText(line.description, product?.name)) ?? null;
    }

    private findPaymentTerm(value?: string | null): PaymentTerm | null {
        return this.paymentTerms.map(option => option.value)
            .find(term => this.matchesAiText(value, term?.name)) ?? null;
    }

    private parseAiDate(value?: string | null): Date | null {
        if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
            return null;
        }
        const date = new Date(`${value.trim()}T00:00:00`);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    private aiNumber(value: number | null | undefined, fallback: number): number {
        return value !== null && value !== undefined && Number.isFinite(Number(value)) ? Number(value) : fallback;
    }

    private aiDerivedUnitPrice(line: AiParsedOrderLine, quantity: number, fallback: number): number {
        const netAmount = this.aiNumber(line.netAmount, this.aiNumber(line.lineTotal, 0));
        return quantity > 0 && netAmount > 0 ? netAmount / quantity : fallback;
    }

    private matchesAiText(value: string | null | undefined, ...candidates: Array<string | null | undefined>): boolean {
        const normalizedValue = this.normalizeAiText(value);
        return !!normalizedValue && candidates.some(candidate => normalizedValue === this.normalizeAiText(candidate));
    }

    private normalizeAiText(value: string | null | undefined): string {
        return (value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    }

    private minimalCrmClientRef(id?: number | null): CrmClient | undefined {
        return id ? ({ id } as CrmClient) : undefined;
    }

    private minimalCustomerRef(id?: number | null): Customer | undefined {
        return id ? ({ id } as Customer) : undefined;
    }

    private minimalCompanyRef(id?: number | null): Company | undefined {
        return id ? ({ id } as Company) : undefined;
    }

    private minimalPaymentTermRef(paymentTermID?: number | null): PaymentTerm | undefined {
        return paymentTermID ? ({ paymentTermID } as PaymentTerm) : undefined;
    }

    private minimalProductRef(productID?: number | null): Products | undefined {
        return productID ? ({ productID } as Products) : undefined;
    }

    private minimalVariantRef(variantID?: number | null): ProductVariant | undefined {
        return variantID ? ({ variantID } as ProductVariant) : undefined;
    }

    private defaultCurrencyCode(): string {
        const company = this.sessionStorageService.getCompany();
        const currency = this.sessionStorageService.getCountryList()
            ?.find(country => country.id === company?.countryId)?.currencyCode;
        return this.normalizeCurrencyCode(currency);
    }

    private syncVariantControl(lineGroup: FormGroup): void {
        const variantControl = lineGroup.get('variant');
        if (!variantControl) {
            return;
        }
        variantControl.enable({ emitEvent: false });
        variantControl.setValidators([Validators.required]);
        variantControl.updateValueAndValidity({ emitEvent: false });
    }

    private resolveSalesUnitPrice(product: Products | null | undefined, variant?: ProductVariant | null): number {
        return this.resolveAmount(
            variant?.sellingRateAdjustment,
            variant?.rateAdjustment,
            product?.sellingRate,
            product?.standardRate,
            0
        );
    }

    private resolveAmount(...candidates: Array<number | null | undefined>): number {
        for (const candidate of candidates) {
            if (candidate !== null && candidate !== undefined && !Number.isNaN(Number(candidate))) {
                return Number(candidate);
            }
        }
        return 0;
    }

    private normalizeCurrencyCode(value: string | null | undefined): string {
        const normalized = (value ?? '').toString().trim().toUpperCase();
        if (!normalized || normalized === 'NULL' || normalized === 'UNDEFINED') {
            return 'SAR';
        }
        return normalized;
    }

    private findExistingLine(line: SalesOrderLine): SalesOrderLine | undefined {
        const lines = this.currentSalesOrder?.salesOrderLines || [];
        return lines.find(existing => existing.lineID && existing.lineID === line.lineID)
            || lines.find(existing => existing.sourceQuotationLineId && existing.sourceQuotationLineId === line.sourceQuotationLineId);
    }

    private hasMissingSourcePriceOverrideReason(): boolean {
        return this.getSalesOrderLineControls.controls.some(control => {
            const value = control.getRawValue();
            const existingLine = this.findExistingLine(value as SalesOrderLine);
            if (!existingLine?.sourceQuotationLineId) {
                return false;
            }
            const changed = Number(value.unitPrice || 0) !== Number(existingLine.unitPrice || 0);
            return changed && !(value.priceOverrideReason || '').trim();
        });
    }

    private handleSaveSuccess(savedOrder?: SalesOrder): void {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: `Sales order ${this.editMode ? 'updated' : 'created'} successfully` });
        const complete = () => {
            this.isLoading = false;
            this.submitted = false;
            this.salesOrderForm.enable();
            const savedOrderId = Number(savedOrder?.orderID || this.salesOrderId || 0);
            if (!this.editMode && savedOrderId) {
                this.router.navigate(['/order/sales-order/view', savedOrderId]);
                return;
            }
            if (!this.editMode) {
                this.salesOrderForm.reset();
                this.initForm(); // Re-initialize to set default values
            }
            this.router.navigate(['/order/sales-order']);
        };
        const savedOrderId = Number(savedOrder?.orderID || this.salesOrderId || 0);
        this.persistAiSourceDocument(savedOrderId).subscribe({
            next: () => this.linkProjectRequirementFulfillment(savedOrder).add(complete),
            error: error => {
                this.messageService.add({ severity: 'warn', summary: 'Source document', detail: error?.error?.message || 'Sales order was saved, but its AI source document could not be attached.' });
                this.linkProjectRequirementFulfillment(savedOrder).add(complete);
            }
        });
    }

    private persistAiSourceDocument(orderId: number) {
        const file = this.pendingAiSourceDocument;
        const owningCompanyId = Number(this.currentOwningCompanyId || this.sessionStorageService.getCompany()?.id || 0);
        const uploadedBy = Number(this.sessionStorageService.getUser()?.id || 0);
        if (!file || !orderId || !owningCompanyId || !uploadedBy) return of(void 0);
        return this.firebaseStorageService.uploadFileAndGetPublicUrl(file, `orders/sales-orders/${orderId}/attachments`).pipe(
            switchMap(filePath => this.salesOrderService.createSalesOrderAttachment(orderId, owningCompanyId, {
                attachmentId: 0, orderId, owningCompanyId, fileName: file.name, filePath,
                fileType: file.type || 'application/pdf', fileSize: file.size, uploadedAt: new Date(), uploadedBy,
                fileContent: 'Source document uploaded for AI extraction'
            } as SalesOrderAttachment)),
            tap(() => this.pendingAiSourceDocument = undefined),
            map(() => void 0)
        );
    }

    private linkProjectRequirementFulfillment(savedOrder?: SalesOrder) {
        const projectId = Number(this.route.snapshot.queryParamMap.get('projectId') || this.salesOrderForm.get('logisticsProjectId')?.value || 0);
        const requirementId = Number(this.route.snapshot.queryParamMap.get('requirementId') || 0);
        const owningCompanyId = Number(this.currentOwningCompanyId || this.sessionStorageService.getCompany()?.id || 0);
        const targetResourceId = Number(savedOrder?.orderID || this.salesOrderId || 0);
        if (!projectId || !requirementId || !owningCompanyId || !targetResourceId) {
            return { add: (callback: () => void) => callback() };
        }
        return this.logisticsProjectsService.linkRequirementFulfillment(projectId, owningCompanyId, requirementId, {
            targetResourceType: this.route.snapshot.queryParamMap.get('targetResourceType') || 'SALES_ORDER',
            targetResourceId,
            sourceModule: 'ORDER',
            sourceReference: savedOrder?.orderNumber || `Sales order #${targetResourceId}`,
            fulfilledQuantity: Number(this.route.snapshot.queryParamMap.get('quantity') || 1),
            fulfillmentStatus: 'LINKED',
            status: 'ACTIVE'
        }).subscribe({
            error: error => this.messageService.add({
                severity: 'warn',
                summary: 'Requirement link',
                detail: error?.error?.message || 'Sales order was saved, but requirement fulfillment was not linked.'
            })
        });
    }

    private handleSaveError(error: any): void {
        this.messageService.add({
            severity: 'error',
            summary: 'Save failed',
            detail: this.extractErrorDetail(error, 'Unable to save the sales order.')
        });
        this.isLoading = false;
        this.submitted = false;
        this.salesOrderForm.enable();
    }

    private extractErrorDetail(error: any, fallback: string): string {
        return error?.error?.message || error?.error?.error || error?.message || fallback;
    }

    lineAmounts(line: AbstractControl) {
        const quantity = Number(line.get('quantity')?.value || 0);
        const unitPrice = Number(line.get('unitPrice')?.value || 0);
        const product = line.get('product')?.value as Products | null;
        return orderLineAmounts(quantity, unitPrice, Number(product?.taxRate || 0), this.currencyCode,
            Number(line.get('appliedLineDiscount')?.value || 0));
    }

    openPriceCalculator(line: AbstractControl): void {
        this.priceTargetLine = line;
        const amounts = this.lineAmounts(line);
        this.targetNetAmount = roundCurrency(amounts.subtotal - amounts.discount, this.currencyCode);
    }

    get currencyDigits(): number { return currencyFractionDigits(this.currencyCode); }

    priceTargetPreview(): { price: number; net: number; tax: number; total: number } | null {
        const line = this.priceTargetLine;
        const target = this.targetNetAmount;
        if (!line || target === null || target <= 0) return null;
        const quantity = Number(line.get('quantity')?.value);
        const discount = Number(line.get('appliedLineDiscount')?.value || 0);
        const price = unitPriceForTargetNet(quantity, target, discount, this.currencyCode);
        if (price === null) return null;
        const product = line.get('product')?.value as Products | null;
        const amounts = orderLineAmounts(quantity, price, Number(product?.taxRate || 0), this.currencyCode, discount);
        return { price, net: roundCurrency(amounts.subtotal - amounts.discount, this.currencyCode), tax: amounts.tax, total: amounts.total };
    }

    applyTargetPrice(): void {
        const preview = this.priceTargetPreview();
        if (!preview || !this.priceTargetLine) return;
        this.priceTargetLine.get('unitPrice')?.setValue(preview.price);
        this.priceTargetLine.markAsDirty();
        this.priceTargetLine = null;
    }

    calculateTotals(): void {
        let subtotal = 0;
        let discount = 0;
        let totalTax = 0;

        this.getSalesOrderLineControls.controls.forEach(line => {
            const amounts = this.lineAmounts(line);
            subtotal += amounts.subtotal;
            discount += amounts.discount;
            totalTax += amounts.tax;
        });

        this.subtotal = roundCurrency(subtotal, this.currencyCode);
        this.tax = roundCurrency(totalTax, this.currencyCode);
        this.total = roundCurrency(this.subtotal - discount + this.tax, this.currencyCode);
    }

    cancel(): void {
        if (this.salesOrderForm.dirty) {
            this.confirmationService.confirm({
                message: 'You have unsaved changes. Are you sure you want to cancel?',
                header: 'Confirmation',
                icon: 'pi pi-exclamation-triangle',
                accept: () => {
                    this.location.back();
                }
            });
        } else {
            this.location.back();
        }
    }

    openCompanyCreation(): void {
        this.selectedCompany = new Company();
        this.companyFormHeader = 'Create Company';
        this.companyFormSubmitLabel = 'Create';
        this.showCompanyForm = true;
    }

    onCompanyFormClosed(result?: Company | boolean): void {
        this.showCompanyForm = false;
        this.selectedCompany = new Company();
        const savedCompany = result instanceof Company ? result : undefined;
        if (savedCompany) {
            this.loadCompanies().subscribe(() => {
                const currentCompany = this.salesOrderForm.get('company')?.value as Company | null;
                if (currentCompany) {
                    const matching = this.companies.find(item => item.value?.id === currentCompany.id);
                    if (!matching) {
                        this.salesOrderForm.patchValue({ company: currentCompany });
                    }
                }
            });
        }
    }

    openCustomerCreation(): void {
        const company = this.salesOrderForm.get('company')?.value as Company | null;
        if (!company) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Select Company',
                detail: 'Choose a company before creating a customer.'
            });
            return;
        }
        this.selectedCustomer = new Customer();
        this.selectedCompany = company;
        this.customerFormHeader = 'Create Customer';
        this.customerFormSubmitLabel = 'Create';
        this.showCustomerForm = true;
    }

    onCustomerFormClosed(result?: Customer | boolean): void {
        this.showCustomerForm = false;
        this.selectedCustomer = new Customer();
        const savedCustomer = result instanceof Customer ? result : undefined;
        if (savedCustomer) {
            const company = this.salesOrderForm.get('company')?.value as Company | null;
            if (company) {
                this.loadCustomersByCompany({ value: company } as SelectChangeEvent);
            }
        }
    }

    openCreateProductDialog(lineIndex: number): void {
        this.productDialogRef?.close();
        this.productDialogRef = this.dialogService.open(AddEditProductComponent, {
            header: 'Add Product',
            modal: true,
            width: '96vw',
            breakpoints: { '1400px': '96vw', '960px': '98vw', '640px': '100vw' },
            contentStyle: { 'max-height': 'calc(100vh - 7rem)', overflow: 'auto' },
            styleClass: 'order-product-dialog'
        });
        this.productDialogRef.onClose.subscribe((createdProduct: Products | undefined) => {
            this.productDialogRef = undefined;
            if (!createdProduct?.productID) {
                return;
            }
            this.selectCreatedProduct(createdProduct, lineIndex);
        });
    }

    private selectCreatedProduct(product: Products, lineIndex: number): void {
        const existingIndex = this.products.findIndex(option => Number(option.value?.productID) === Number(product.productID));
        const option = { label: product.name, value: product };
        this.products = existingIndex >= 0
            ? this.products.map((item, index) => index === existingIndex ? option : item)
            : [...this.products, option];

        const lineGroup = this.getSalesOrderLineControls.at(lineIndex) as FormGroup | undefined;
        if (!lineGroup) {
            return;
        }
        lineGroup.patchValue({ product }, { emitEvent: false });
        lineGroup.get('product')?.markAsDirty();
        lineGroup.get('product')?.markAsTouched();
        this.onProductChange({ value: product } as SelectChangeEvent, lineIndex);
        this.messageService.add({ severity: 'success', summary: 'Product added', detail: `${product.name} was selected for line ${lineIndex + 1}.` });
    }

    navigateToCreateProductVariant(product?: Products | null): void {
        if (product?.productID) {
            const url = this.router.serializeUrl(this.router.createUrlTree(['/order/products/edit', product.productID]));
            window.open(url, '_blank');
            return;
        }
        this.messageService.add({
            severity: 'warn',
            summary: 'Select Product',
            detail: 'Choose a product before creating variants.'
        });
    }

    private crmClientToCustomerOption(client: CrmClient, roleLabel: string): SelectItem<Customer> {
        const customer = this.crmClientToLinkedCustomer(client);
        const roles = (client.roles || []).join(', ');
        const missingLink = !customer.id ? ' - sync contact required' : '';
        return {
            value: customer,
            label: `${client.companyName || client.notifyingPersonName || 'CRM Client'}${client.clientNumber ? ` (${client.clientNumber})` : ''}${roles ? ` - ${roles}` : ` - ${roleLabel}`}${missingLink}`,
            disabled: !customer.id
        };
    }

    private crmClientToLinkedCustomer(client: CrmClient): Customer {
        const customer = new Customer();
        customer.id = client.linkedCustomerId || 0;
        customer.name = client.companyName || client.notifyingPersonName || `CRM Client ${client.id || ''}`.trim();
        customer.emailAddress = client.primaryEmail || '';
        customer.phoneNumber = client.phoneNumber || client.mobileNumber || '';
        customer.countryId = client.countryId || 0;
        const company = new Company();
        company.id = client.linkedCompanyId || 0;
        company.name = client.companyName || customer.name;
        company.countryId = client.countryId || 0;
        company.emailAddress = client.primaryEmail || null;
        company.phoneNumber = client.phoneNumber || client.mobileNumber || null;
        company.websiteURL = client.website || null;
        company.taxRegNumber = client.taxRegNumber || null;
        company.crNumber = Number(client.crNumber || 0);
        customer.company = company;
        return customer;
    }

    crmActive(): boolean {
        return this.enterpriseFeatureService.getIsCrmSubscribed();
    }

    crmContactLabel(contact: CrmClientCommunication): string {
        return [
            contact.name,
            contact.communicationType,
            contact.email,
            contact.phone || contact.mobile
        ].filter(Boolean).join(' | ') || 'CRM contact';
    }

    crmContactOptions(): SelectItem<number>[] {
        return this.crmClientContacts
            .map(contact => ({
                label: this.crmContactLabel(contact),
                value: contact.id || 0
            }))
            .filter(option => !!option.value);
    }

    selectedCrmContactLabel(): string {
        const selectedId = Number(this.salesOrderForm?.get('crmClientContactId')?.value || 0);
        const selected = this.crmClientContacts.find(contact => contact.id === selectedId);
        if (selected) {
            return this.crmContactLabel(selected);
        }
        return this.crmClientContacts.length ? 'Select a CRM contact' : 'No contacts linked';
    }

    private resolvePreferredCrmContactId(selectedId?: number | null): number | null {
        const normalizedId = Number(selectedId || 0);
        if (normalizedId && this.crmClientContacts.some(contact => contact.id === normalizedId)) {
            return normalizedId;
        }
        return this.crmClientContacts.find(contact => contact.primaryRecord)?.id
            || this.crmClientContacts[0]?.id
            || null;
    }

    private applyPartyValidators(): void {
        const mainPartyControl = this.salesOrderForm?.get('mainParty');
        const companyControl = this.salesOrderForm?.get('company');
        const customerControl = this.salesOrderForm?.get('customer');
        const crmClientControl = this.salesOrderForm?.get('crmClient');
        if (!mainPartyControl || !companyControl || !customerControl || !crmClientControl) {
            return;
        }
        if (this.isCrmPartySource()) {
            mainPartyControl.clearValidators();
            crmClientControl.setValidators([Validators.required]);
        } else {
            mainPartyControl.setValidators([Validators.required]);
            crmClientControl.clearValidators();
        }
        companyControl.clearValidators();
        customerControl.clearValidators();
        mainPartyControl.updateValueAndValidity({ emitEvent: false });
        companyControl.updateValueAndValidity({ emitEvent: false });
        customerControl.updateValueAndValidity({ emitEvent: false });
        crmClientControl.updateValueAndValidity({ emitEvent: false });
    }

    private loadPartyOptionsForMode(): void {
        this.loadMainParties();
        if (this.crmActive()) {
            if (!this.editMode) {
                this.partySourceMode = 'CRM';
                this.applyPartyValidators();
            }
            this.loadCrmClients().subscribe();
        }
    }

    isCrmPartySource(): boolean {
        return this.partySourceMode === 'CRM';
    }

    isManualPartySource(): boolean {
        return this.partySourceMode === 'MANUAL';
    }

    setPartySourceMode(mode: PartySourceMode): void {
        if (mode === 'CRM' && !this.crmActive()) {
            return;
        }
        this.partySourceMode = mode;
        if (mode === 'CRM') {
            this.salesOrderForm.patchValue({ mainParty: null, subParty: null, company: null, customer: null }, { emitEvent: false });
            this.loadCrmClients().subscribe();
        } else {
            this.crmClientContacts = [];
            this.salesOrderForm.patchValue({
                crmClient: null,
                crmClientContactId: null,
                mainParty: null,
                subParty: null,
                company: null,
                customer: null
            }, { emitEvent: false });
            this.loadMainParties();
        }
        this.applyPartyValidators();
        this.refreshClientItemCodes();
    }

    private minimalPartyRef(party?: Party | null): Party | undefined {
        if (!party?.id?.entityId || !party.id.entityType) {
            return undefined;
        }
        return {
            id: { entityId: party.id.entityId, entityType: party.id.entityType },
            owningCompanyId: this.sessionStorageService.getCompany()?.id ?? 0,
            entityName: party.entityName,
            isCompany: party.isCompany
        };
    }
}
